// hooks/useSummaryData.ts
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  createRow,
  getOptionLabel,
  formatAirflowValue,
  formatSectionType,
  bot_coil_types,
  bot_sa_configurations,
} from '../helpers/data/bot';
import {
  BotSection,
  BotTunnel,
  SummaryRow,
  SummarySection,
  Option,
  UseSummaryDataProps,
} from '../models/bot';
import type { IState } from '../../../models';
import { formattedNumber, toDecimal } from '../helpers/index';
// ------------------------------------------------
// -------------------- HOOK ----------------------
// ------------------------------------------------
export const useSummaryData = ({
  data,
  params,
  fanParams,
  coil_params,
  conn_hands,
  filterParams,
  bot_tunnel_configurations,
  bot_tunnel_configurations_all,
}: UseSummaryDataProps): SummarySection[] => {
  const openingParams = useSelector((state: IState) => state.opening.params);
  const mounting_planes = openingParams?.mounting_plane ?? [];
  const weatherRatingLabel = useMemo(() => {
    const opts: Option[] = params?.weather_rating ?? [];
    const found = opts.find((o) => o.id === data.weather_rating_id);
    return found?.name ?? '';
  }, [data.weather_rating_id, params?.weather_rating]);
  const airSystemLabel = useMemo(() => {
    const opts: Option[] = params?.air?.air_system ?? [];
    const found = opts.find((o) => o.id === data.air_system_id);
    return found?.name ?? found?.label ?? '';
  }, [data.air_system_id, params?.air?.air_system]);
  const tunnelOptions: Option[] = useMemo(() => {
    const base =
      data.air_system_id !== 5
        ? bot_tunnel_configurations
        : bot_tunnel_configurations_all;
    if (!params?.tunnels?.configurations?.length) return base;
    const allowed = new Set(
      params.tunnels.configurations.map((c: any) => c.id),
    );
    return base.filter((opt) => allowed.has(opt.id));
  }, [
    data.air_system_id,
    params?.tunnels?.configurations,
    bot_tunnel_configurations,
    bot_tunnel_configurations_all,
  ]);
  const tunnelConfigurationLabel = useMemo(() => {
    const match = tunnelOptions.find(
      (opt) => opt.id === data.tunnel_configuration,
    );
    return match?.label ?? '';
  }, [data.tunnel_configuration, tunnelOptions]);
  const saOptions: Option[] = useMemo(
    () =>
      bot_sa_configurations[
        data.air_system_id as keyof typeof bot_sa_configurations
      ]?.[
        data.tunnel_configuration as keyof (typeof bot_sa_configurations)[keyof typeof bot_sa_configurations]
      ]?.sa_ea_configurations ?? [],
    [data.air_system_id, data.tunnel_configuration, bot_sa_configurations],
  );
  const saConfigurationLabel = useMemo(() => {
    const match = saOptions?.find(
      (opt) => opt.value === data.sa_ea_intake_configuration,
    );
    return match?.label ?? '';
  }, [data.sa_ea_intake_configuration, saOptions]);
  const airStreamOptions: Option[] = useMemo(
    () => params?.air?.air_stream ?? fanParams?.air_stream ?? [],
    [params?.air?.air_stream, fanParams?.air_stream],
  );
  const airflowDirections = useMemo(
    () =>
      (data.tunnels ?? []).map((t: BotTunnel, i: number) => {
        const path = (t.sections ?? [])
          .filter((s) => s.type)
          .map((s) => formatSectionType(s.type ?? ''))
          .join(' → ');
        return {
          id: t.id ?? `tunnel-${i}`,
          label: t.index || `Tunnel ${i + 1}`,
          path: path || 'No sections configured yet',
        };
      }),
    [data.tunnels],
  );
  const generalRows = useMemo<SummaryRow[]>(() => {
    return [
      createRow('AHU Tag', data.tag || '- -'),
      ...(data.hasSupplyAir
        ? [
            createRow(
              'Airflow Supply',
              formattedNumber(data.sa_volume_max, undefined, true, 'number'),
              'CFM',
            ),
          ]
        : []),
      ...(data.hasOutsideAir
        ? [
            createRow(
              'Airflow Outside',
              formattedNumber(data.oa_volume_max, undefined, true, 'number'),
              'CFM',
            ),
          ]
        : []),
      ...(data.hasReturnAir
        ? [
            createRow(
              'Airflow Return',
              formattedNumber(data.ra_volume_max, undefined, true, 'number'),
              'CFM',
            ),
          ]
        : []),
      ...(data.hasExhaustAir
        ? [
            createRow(
              'Airflow Exhaust',
              formattedNumber(data.ea_volume_max, undefined, true, 'number'),
              'CFM',
            ),
          ]
        : []),
      createRow('Weather Rating', weatherRatingLabel),
      createRow('Air System', airSystemLabel),
      createRow('Tunnel Configuration', tunnelConfigurationLabel),
      createRow('Airflow Configuration', saConfigurationLabel),
    ];
  }, [
    data.tag,
    data.sa_volume_max,
    data.ra_volume_max,
    data.ea_volume_max,
    weatherRatingLabel,
    airSystemLabel,
    tunnelConfigurationLabel,
    airflowDirections,
    saConfigurationLabel,
  ]);
  const tunnelSummaries = useMemo<SummarySection[]>(() => {
    const tunnels: BotTunnel[] = data.tunnels ?? [];
    return tunnels.map((tunnel) => {
      const tunnelLabel = `Tunnel ${tunnel.tunnel_index}`;
      const rows: SummaryRow[] = [];
      (tunnel.sections ?? []).forEach((sec: BotSection) => {
        if (!sec.type) return;
        const airStream = getOptionLabel(airStreamOptions, sec.air_stream_id);
        const sectionLabel = `${sec.name}`;
        // ----- ROTARY HTEX -----
        if (sec.type === 'er_wheel') {
          rows.push(
            createRow(
              sectionLabel,
              `${formatAirflowValue(sec.airflow)}`,
              'CFM',
            ),
          );
        }
        // ----- FAN -----
        if (sec.type === 'fan') {
          rows.push(
            createRow(
              sectionLabel,
              `${formatAirflowValue(sec.airflow)} @ ${toDecimal(
                sec.tsp || 0,
              )} in WC`,
              'CFM',
            ),
          );
        }
        // ----- COIL -----
        if (sec.type === 'coil') {
          const parts = [
            airStream,
            getOptionLabel(coil_params?.duty, sec.duty_id),
            getOptionLabel(coil_params?.htfluid, sec.htfluid_id),
            getOptionLabel(conn_hands, sec.conn_hand_id),
            getOptionLabel(bot_coil_types, sec.type_id),
          ].filter(Boolean);
          rows.push(
            createRow(
              sectionLabel,
              `${parts[0]}, ${parts[4]}, ${parts[1]}, ${parts[2]}`,
            ),
          );
        }
        // ----- FILTER -----
        if (sec.type === 'filter') {
          const parts = [
            getOptionLabel(filterParams?.nominal_depth, sec.depth_nominal_id),
            getOptionLabel(filterParams?.merv, sec.merv_id),
            getOptionLabel(filterParams?.type, sec.type_id),
            getOptionLabel(filterParams?.frame?.loading, sec.frame_loading_id),
            airStream,
          ].filter(Boolean);
          rows.push(
            createRow(
              sectionLabel,
              parts.length
                ? `${parts[0]} in, MERV ${parts[1]}, ${parts[2]}`
                : 'Incomplete filter data',
            ),
          );
        }
        if (sec.type === 'plenum') {
          // ----- DEFAULT -----
          rows.push(createRow(sectionLabel, ''));
        }
        sec.accessories?.forEach((acc) => {
          let accessoryValue = '';
          if (
            (acc.type === 'access_door' || acc.type === 'opening') &&
            acc.mounting_plane_id
          ) {
            accessoryValue =
              getOptionLabel(mounting_planes, acc.mounting_plane_id) || '';
          } else if (acc.type === 'drain_pan' && acc.conn_hand_id) {
            accessoryValue = `${getOptionLabel(conn_hands, acc.conn_hand_id) || ''} connection`;
          }
          rows.push(createRow(`${acc.name} `, accessoryValue, '', true));
        });
      });

      return { title: tunnelLabel, rows };
    });
  }, [
    data.tunnels,
    airStreamOptions,
    coil_params,
    conn_hands,
    filterParams,
    mounting_planes,
  ]);
  return useMemo<SummarySection[]>(
    () => [{ title: '', rows: generalRows }, ...tunnelSummaries],
    [generalRows, tunnelSummaries],
  );
};
